import {ReactNode, startTransition, useCallback, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import {EmblaCarouselType} from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import {throttle} from '@zzng/common/utils/throttle';

import {Card} from '@/root/models/card';
import {useEmblaStore, useExpiredNoticeStore, useUtilButtonsStore} from '@/root/stores/cardCarousel';

import {isExpired} from '@/shared/utils/dateFormat';

const SCALE_FACTOR = 0.9; // NOTE: 마크업에서 정의한 수치
const CAROUSEL_CONTAINER_CLASSNAME = '__container_carousel';
const CAROUSEL_SLIDE_CLASSNAME = '__carousel_slide';
const CARD_CLICK_DELAY = 100;

interface Props {
  cards: Card[];
  children: (card: Card, index: number, selectedCardIndex: number) => ReactNode;
  startIndex?: number;
  onSwipe?: (emblaApi: EmblaCarouselType) => void;
}

const CardCarousel = ({cards, children, startIndex = 0, onSwipe}: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: false,
    container: CAROUSEL_CONTAINER_CLASSNAME,
    skipSnaps: true,
    active: cards.length > 1,
    startIndex,
    duration: 18 // 딜런과 합의한 속도 값
  });

  const [selectedCardIndex, setSelectedCardIndex] = useState(startIndex);

  const cardElements = useRef<HTMLElement[]>([]);
  const isCarouselTouching = useRef(false);
  const isCarouselTouchingDelayTimer = useRef<ReturnType<typeof setTimeout>>();

  const setVisibleExpiredNotice = useExpiredNoticeStore((state) => state.setVisible);
  const setEmblaApi = useEmblaStore((state) => state.setEmblaApi);
  const setUtilButtons = useUtilButtonsStore((state) => state.setUtilButtons);

  const initCardElements = useCallback((emblaApi: EmblaCarouselType) => {
    cardElements.current = emblaApi.slideNodes().map((node) => {
      const card = node.firstChild as HTMLElement;
      return card;
    });
  }, []);

  const updateCardsScale = useCallback((emblaApi: EmblaCarouselType) => {
    // 현재 캐러셀의 스크롤 진행도 (0 ~ 카드 개수 사이의 부동소수값)
    const scrollProgress = emblaApi.scrollProgress() * (cardElements.current.length - 1);
    const cardsInView = emblaApi.slidesInView();

    cardsInView.forEach((cardIndex) => {
      const card = cardElements.current[cardIndex];
      const distanceToCenter = Math.abs(cardIndex - scrollProgress);

      const scale = SCALE_FACTOR + (1 - SCALE_FACTOR) * Math.max(0, 1 - distanceToCenter);
      card.style.transform = `scale(${scale})`;
    });
  }, []);

  const updateExpiredCardNotice = useCallback(
    throttle((emblaApi: EmblaCarouselType) => {
      if (isCarouselTouching.current) {
        setVisibleExpiredNotice(false);
        return;
      }
      startTransition(() => {
        const currentCard = cards[emblaApi.selectedScrollSnap()];
        setVisibleExpiredNotice(isExpired(currentCard.validEndAt));
      });
    }),
    []
  );

  const updateUtilButtons = useCallback((emblaApi: EmblaCarouselType) => {
    startTransition(() => {
      const currentCard = cards[emblaApi.selectedScrollSnap()];
      setUtilButtons(currentCard.buttons);
    });
  }, []);

  const updateSelectedCard = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedCardIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onPointerDown = useCallback(() => {
    // 카드 클릭과 드래그 이벤트를 구분을 위한 타이머
    isCarouselTouchingDelayTimer.current = setTimeout(() => {
      isCarouselTouching.current = true;
    }, CARD_CLICK_DELAY);
  }, []);

  const onPointerUp = useCallback((emblaApi: EmblaCarouselType) => {
    onSwipe?.(emblaApi);
    clearTimeout(isCarouselTouchingDelayTimer.current);
    isCarouselTouching.current = false;
    emblaApi.scrollTo(emblaApi.selectedScrollSnap());
  }, []);

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    updateCardsScale(emblaApi);
    updateExpiredCardNotice(emblaApi);
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    updateSelectedCard(emblaApi);
    updateUtilButtons(emblaApi);
    updateExpiredCardNotice(emblaApi);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const init = (emblaApi: EmblaCarouselType) => {
      setEmblaApi(emblaApi);
      initCardElements(emblaApi);
      updateUtilButtons(emblaApi);
      updateExpiredCardNotice(emblaApi);
    };

    init(emblaApi);

    emblaApi
      .on('pointerDown', onPointerDown)
      .on('pointerUp', onPointerUp)
      .on('scroll', onScroll)
      .on('select', onSelect);

    return () => {
      emblaApi
        .off('pointerDown', onPointerDown)
        .off('pointerUp', onPointerUp)
        .off('scroll', onScroll)
        .off('select', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    // unmount 시 스토어 초기화
    return () => {
      setVisibleExpiredNotice(false);
      setUtilButtons([]);
      setEmblaApi(undefined);
    };
  }, []);

  return (
    <div ref={emblaRef}>
      <div
        className={cn('wrap_card', CAROUSEL_CONTAINER_CLASSNAME)}
        style={cards.length === 1 ? {display: 'flex', justifyContent: 'center'} : {}}
      >
        {cards.map((card, index) => (
          <div className={CAROUSEL_SLIDE_CLASSNAME} key={card.cardId}>
            {children(card, index, selectedCardIndex)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardCarousel;
